/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */
// React
import {
	useEffect,
	useLayoutEffect,
	useRef,
	useState,
} from 'react';

// Mantine
import {
	Box,
	Group,
	Image,
	Paper,
	Skeleton,
	Text,
} from '@mantine/core';
import { useElementSize } from '@mantine/hooks';

// Styles
import classes from './mint-history.module.css';

// Components
import { AddFiltersButton } from '@components/atoms/AddFiltersButton';
import { Button } from '@components/atoms/Button';
import { DateFilter } from '@components/molecules/DateFilter';
import { MenuFilter } from '@components/molecules/MenuFilter/MenuFilter';
import { RowMintHistory } from '@components/molecules/RowMintHistory';

// I18n
import { useTranslation } from 'react-i18next';

// Icons
import ArrowScrollIcon from '@assets/icons/arrow-scroll.svg';
import { IconPlayerPlay } from '@tabler/icons-react';

// Apis
import { useMintHistories } from '@features/sourcing/mint-history/api/get-mint-histories';
import { useSites } from 'shared/api/get-sites';

// Store
import useStore from '@store/index';

// Icons
import NoRecordsIcon from '@assets/icons/no-records-datatable.svg';

// Utils
import {
	convertArrayToLabelValue,
	uuid,
} from '@utils/functions';
import { MINT_STATUS_TRANSACTION_FILTER_OPTIONS } from '@utils/options';

import { Pagination } from '@components/atoms/Pagination';
import { ROLES } from '@config/constants/roles';
import { DrawerMintHistoryDetails } from '@features/sourcing/mint-history/components/DrawerMintHistoryDetails/DrawerMintHistoryDetails';
import { useMintHistoryProductions } from '@features/sourcing/mint-history/hooks/useMintHistoryProductions';
import { TMintHistoryModified } from '@features/sourcing/mint-history/types';
import { TSelectOption } from '@global-types/index';
import { useAuthorization } from '@lib/authorization';
import dayjs from 'dayjs';
import { useMeasurabilities } from 'shared/api/get-measurabilities';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */
const MintHistory = () => {
	const { t } = useTranslation();
	const [isOverflowFilters] = useState(false);
	const [showScrollFilters, setShowScrollFilters] =
		useState(false);
	const [resetFilterSites, setResetFilterSites] =
		useState(false);
	const [queryParamsProjects, setQueryParamsProjects] =
		useState({});
	const containerRef = useRef<HTMLDivElement>(null);

	const {
		setMintHistoriesPage,
		mintHistoriesQueryParams,
		setMintHistoriesQueryParams,
		setMintHistoriesPageSize,
		setIsOpenStartMint,
		setProductionsStartMintsActiveStep,
		setProductionsStartMintsSelectedRecords,
		setMintHistorySelected,
		mintHistoriesPageSize,
		mintHistoriesPage,
	} = useStore();

	const [, , handleUpdateProductions] =
		useMintHistoryProductions();

	// APIs
	const { data: projects } = useMeasurabilities();
	const { data: sites } = useSites(queryParamsProjects);
	const {
		data: mintHistories,
		isLoading,
		total: totalItems,
		totalPages,
	} = useMintHistories(
		mintHistoriesPage,
		mintHistoriesPageSize,
		mintHistoriesQueryParams
	);

	const handleReset = () => {
		setMintHistoriesPageSize(10);
		setMintHistoriesQueryParams({});
		setMintHistoriesPage(1);
	};

	useEffect(() => {
		return () => {
			handleReset();
		};
	}, []);

	const { ref, width: widthContainer } = useElementSize();

	useLayoutEffect(() => {
		if (
			containerRef.current &&
			containerRef.current.clientWidth <
				containerRef.current.scrollWidth
		) {
			setShowScrollFilters(true);
		} else {
			setShowScrollFilters(false);
		}
	}, [widthContainer]);

	const handleScroll = (scrollTo: string) => {
		if (containerRef.current) {
			if (scrollTo === 'right') {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft +
					containerRef.current.offsetWidth;
			} else {
				containerRef.current.scrollLeft =
					containerRef.current.scrollLeft -
					containerRef.current.offsetWidth;
			}
		}
	};

	const handleStartMint = () => {
		setProductionsStartMintsActiveStep(0);
		setIsOpenStartMint(true);
	};

	const [isOpen, setIsOpen] = useState(false);
	const { checkRole } = useAuthorization();

	return (
		<Box className={classes.container} ref={ref}>
			<Paper className={classes.paper}>
				{/* Drawer mint history details */}
				<DrawerMintHistoryDetails
					onClose={() => {
						setIsOpen(false);
					}}
					opened={isOpen}
				/>
				{/* Row filters */}
				<div className={classes.containerFilters}>
					<div className={classes.wrapperRowLeft}>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('left');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToLeft
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
						<div
							ref={containerRef}
							className={classes.wrapperFilters}
						>
							<Group gap={'0px'}>
								<Box
									className={classes.containerFilters5}
								>
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<DateFilter
											onApply={(date) => {
												let startDate;
												let endDate;
												if (
													dayjs(
														date[0].value[0]
													).isValid() &&
													dayjs(
														date[0].value[1]
													).isValid()
												) {
													startDate = date[0].value[0];
													endDate = date[0].value[1];
												} else {
													startDate = dayjs()
														.subtract(364, 'days')
														.toISOString();
													endDate = dayjs().toISOString();
												}

												const queryParamsDate = {
													createdAt: {
														$gte: startDate,
														$lt: endDate,
													},
												};

												setMintHistoriesQueryParams({
													...mintHistoriesQueryParams,
													...queryParamsDate,
												});
												setMintHistoriesPage(1);
											}}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={(projects) => {
												const queryParamsProjects = {
													project: projects.map(
														(_project) => _project.value
													),
													site: [],
												};
												setQueryParamsProjects(
													queryParamsProjects
												);
												setMintHistoriesQueryParams({
													...mintHistoriesQueryParams,
													...queryParamsProjects,
												});
												setResetFilterSites(true);
											}}
											label={t('global.project')}
											size="lg"
											options={convertArrayToLabelValue(
												projects
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchProject'
											)}
											placeholder={t(
												'filters.placeholders.selectProject'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											reset={resetFilterSites}
											handlereset={(
												_isReset: boolean
											) => {
												setResetFilterSites(_isReset);
											}}
											onApply={(sites) => {
												setMintHistoriesPage(1);
												const queryParamsSites = {
													site: sites.map(
														(_site) => _site.value
													),
												};

												setMintHistoriesQueryParams({
													...mintHistoriesQueryParams,
													...queryParamsSites,
												});
											}}
											label={t('global.sites')}
											size="lg"
											options={convertArrayToLabelValue(
												sites
											)}
											placeholderSearch={t(
												'filters.placeholderSearch.searchSite'
											)}
											placeholder={t(
												'filters.placeholders.selectSite'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterSelect
										}
									>
										<MenuFilter
											onApply={(
												mintStatusList: TSelectOption[]
											) => {
												const queryParamsMintStatus = {
													status: Array.isArray(
														mintStatusList
													)
														? mintStatusList.map(
																(
																	_mintStatus: TSelectOption
																) => _mintStatus.value
																// eslint-disable-next-line no-mixed-spaces-and-tabs
															)
														: [],
												};
												setMintHistoriesQueryParams({
													...mintHistoriesQueryParams,
													...queryParamsMintStatus,
												});
											}}
											label={t('global.mintStatus')}
											size="lg"
											options={
												MINT_STATUS_TRANSACTION_FILTER_OPTIONS
											}
											placeholderSearch={t(
												'filters.placeholderSearch.searchMintStatus'
											)}
											placeholder={t(
												'filters.placeholders.selectMintStatus'
											)}
										/>
									</Box>
									<Box className={classes.spacerFilter} />
									<Box
										className={
											classes.containerFilterNumeric
										}
									>
										<AddFiltersButton>
											{t('global.addFilters')}
										</AddFiltersButton>
									</Box>
								</Box>
							</Group>
						</div>
						{!isOverflowFilters && showScrollFilters && (
							<Box
								className={
									classes.wrapperIconScrollToRight
								}
								variant="transparent"
								onClick={() => {
									handleScroll('right');
								}}
							>
								<Image
									className={
										classes.containerIconScrollToRight
									}
									src={ArrowScrollIcon}
								/>
							</Box>
						)}
					</div>
					{checkRole([
						ROLES.ADMIN,
						ROLES.SUPERADMIN,
						ROLES.STAFF,
					]) && (
						<div className={classes.wrapperRowRight}>
							<Button
								leftSection={<IconPlayerPlay size={23} />}
								hiddenFrom="xxl"
								size="md"
								onClick={handleStartMint}
							>
								{t('global.startMint')}
							</Button>
							<Button
								leftSection={<IconPlayerPlay size={23} />}
								size="lg"
								visibleFrom="xxl"
								onClick={handleStartMint}
							>
								{t('global.startMint')}
							</Button>
						</div>
					)}
				</div>
				<Box className={classes.spacerRow} />
				<Box className={classes.containerRow}>
					<Group
						gap={'10px'}
						className={classes.containerRowLeft}
					>
						<Box
							className={classes.containerNbrSelection}
						>
							<Text
								className={classes.totalMint}
								size="xs"
							>
								{'('}
								{totalItems &&
									!isNaN(+totalItems) &&
									totalItems}
								{') '}
								{t('global.mint')}
							</Text>
						</Box>
					</Group>
				</Box>
				<Box className={classes.spacerRow} />
				{/* List mint */}
				{isLoading ? (
					<Box className={classes.containerList}>
						<Skeleton height={100} radius="xs" />
						<Skeleton height={100} mt={15} radius="xs" />
						<Skeleton height={100} mt={15} radius="xs" />
						<Skeleton height={100} mt={15} radius="xs" />
					</Box>
				) : (
					<Box className={classes.containerList}>
						{!isLoading &&
						mintHistories &&
						Array.isArray(mintHistories) &&
						mintHistories.length > 0 ? (
							mintHistories.map(
								(item: TMintHistoryModified) => (
									<RowMintHistory
										key={uuid()}
										data={item}
										verified={
											item.pin.status == 'BYPASSED' &&
											item.mint.status == 'BYPASSED' &&
											item.status == 'BYPASSED'
										}
										handleviewdetails={() => {
											setMintHistorySelected(item);
											setProductionsStartMintsSelectedRecords(
												[]
											);
											setIsOpen(true);
										}}
									/>
								)
							)
						) : (
							<Box className={classes.containerEmptyList}>
								<Box
									className={
										classes.containerTextsNoRecords
									}
								>
									<Image
										src={NoRecordsIcon}
										className={classes.iconNoRecords}
									/>
									<>
										<Text
											className={classes.titleNoRecords}
										>
											{t(
												'global.datatable.noResultsFound'
											)}
										</Text>
										<Text
											className={
												classes.descriptionNoRecords
											}
										>
											{t(
												'global.datatable.noResultsFoundDescription'
											)}
										</Text>
									</>
								</Box>
							</Box>
						)}
					</Box>
				)}
			</Paper>
			<Box className={classes.containerPagination}>
				<Pagination
					pagesize={mintHistoriesPageSize}
					totalitems={totalItems}
					activepage={mintHistoriesPage}
					handlechangesize={function (
						_size: string | number
					) {
						setMintHistoriesPageSize(_size);
						setMintHistoriesPage(1);
					}}
					onChange={(data) => {
						setMintHistoriesPage(data);
					}}
					total={totalPages ? totalPages : 0}
				/>
			</Box>
		</Box>
	);
};

export default MintHistory;
